import { Children, forwardRef, ReactNode, useMemo } from 'react';
import {
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  useFloating,
} from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { Heading } from '../../';
import { StylesProp } from '../../../helpers';
import { PopoverMenuStyles } from './popover-menu.styles';
import { UsePopoverMenuResponse } from './use-popover-menu';

const headingLightStyle = css`
  color: ${theme.colors.black};
`;

const headingDarkStyle = css`
  color: ${theme.colors.white};
`;

export type PopoverMenuProps<T extends HTMLElement = HTMLMenuElement> = ReturnType<
  UsePopoverMenuResponse<T>['getMenuProps']
> & {
  title?: string;
  initialFocus?: Parameters<typeof FloatingFocusManager>[0]['initialFocus'];
  children: ReactNode;
  returnFocus?: boolean;
  alwaysRender?: boolean;
  darkMode?: boolean;
  className?: string;
  css?: StylesProp;
};

export const createPopoverMenuVariants = (alwaysRender: boolean) => ({
  initial: {
    opacity: 0,
    transform: 'translateY(8px)',
    ...(alwaysRender && { display: 'none' }),
  },
  visible: {
    opacity: 1,
    transform: 'translateY(0)',
    ...(alwaysRender && { display: 'block' }),
  },
  hidden: {
    opacity: 0,
    transform: 'translateY(8px)',
    ...(alwaysRender && { display: 'none' }),
  },
});

// eslint-disable-next-line react/display-name
export const PopoverMenu = forwardRef<HTMLMenuElement, PopoverMenuProps>(
  (
    {
      children,
      context,
      isOpen,
      title,
      initialFocus,
      returnFocus,
      alwaysRender,
      listItemsRef,
      listContentRef,
      darkMode = false,
      className,
      css: customCss,
      ...popoverProps
    },
    ref
  ) => {
    const styles = PopoverMenuStyles.menu(darkMode);
    useFloating({ nodeId: popoverProps.nodeId });
    const { onAnimationStart, onDragStart, onDragEnd, onDrag, nodeId, ...motionCompatibleProps } = popoverProps;
    const count = Children.count(children);

    const variants = useMemo(() => createPopoverMenuVariants(!!alwaysRender), [alwaysRender]);

    const shouldOpen = isOpen && count > 0;

    return (
      <FloatingNode id={nodeId}>
        <FloatingList elementsRef={listItemsRef} labelsRef={listContentRef}>
          <FloatingPortal>
            <ConditionalRender condition={shouldOpen || !!alwaysRender}>
              <FloatingFocusManager
                disabled={!shouldOpen}
                context={context}
                initialFocus={initialFocus ?? -1}
                returnFocus={returnFocus}
              >
                <FloatingTree>
                  <motion.menu
                    className={cx(styles, className)}
                    css={customCss}
                    variants={variants}
                    initial='initial'
                    animate={shouldOpen ? 'visible' : 'hidden'}
                    exit={'hidden'}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    ref={ref}
                    {...motionCompatibleProps}
                  >
                    {!!title && (
                      <Heading level={2} className={darkMode ? headingDarkStyle : headingLightStyle}>
                        {title}
                      </Heading>
                    )}
                    {children}
                  </motion.menu>
                </FloatingTree>
              </FloatingFocusManager>
            </ConditionalRender>
          </FloatingPortal>
        </FloatingList>
      </FloatingNode>
    );
  }
);

const ConditionalRender = ({ children, condition }: { children: ReactNode; condition: boolean }) => (
  <AnimatePresence>{condition ? <>{children}</> : null}</AnimatePresence>
);
